import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { ForwardConversationMessage200Response } from '@nora-monorepo/nora-api-client';
import { useApiStore } from '@nora-monorepo/ui-shared';

import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';

export const useForwardMessageStore = defineStore('forwardMessageStore', () => {
  const showForwardMessageModal = ref(false);
  const conversationId = ref('');
  const messageId = ref('');
  const isSending = ref(false);

  const api = useApiStore();
  const conversationStore = useConversationStore();
  const conversationMessageStore = useConversationMessageStore();

  function openForwardMessageModal(newConversationId = '', newMessageId = ''): void {
    conversationId.value = newConversationId;
    messageId.value = newMessageId;
    showForwardMessageModal.value = true;
  }

  async function forwardMessages(ids: string[]): Promise<ForwardConversationMessage200Response | undefined> {
    if (!conversationId.value || !messageId.value) {
      return;
    }

    isSending.value = true;

    const message = conversationMessageStore.getConversationMessage(
      conversationId.value,
      messageId.value,
    );

    try {
      const response = await api.unwrap(
        api.conversationMessage.forwardConversationMessage({
          conversationId: conversationId.value,
          messageId: messageId.value,
          messageForwardPayload: {
            conversations: ids,
          },
        }),
      );

      isSending.value = false;

      return response;
    }
    catch (error) {
      isSending.value = false;

      if (message) {
        ids.forEach((id) => {
          const messageCopy = {
            ...message,
            conversationId: id,
          };
          messageCopy.status = 'error';
          conversationStore.updateMessage(messageCopy);
        });
      }

      console.error(error);

      throw new Error('Failed to send string array');
    }
  }

  return {
    showForwardMessageModal,
    conversationId,
    messageId,
    isSending,

    openForwardMessageModal,
    forwardMessages,
  };
});
